// Packages
import { useState, useEffect, useCallback } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate, useParams } from "react-router-dom";
import debounce from "lodash.debounce";

// Helpers
import useHelpers from "../../../../helpers";

// Controllers
import useControllers from "../../..";

// Actions
import useApi from "../../../../api";

const useNotificationCreate = (viewMode = false) => {
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [listCountries, setListCountries] = useState([]);
  const [listGroups, setListGroups] = useState([]);
  const [listEntities, setListEntities] = useState([]);
  const [listEntitiesById, setListEntitiesById] = useState([]);
  const [entitySelected, setEntitySelected] = useState(null);
  const [selectedGroupName, setSelectedGroupName] = useState(null);
  const [selectedGroupType, setSelectedGroupType] = useState(null);
  const [selectedGroupId, setSelectedGroupId] = useState(null);

  const navigation = useNavigate();

  // Helpers
  const { useQuickFunctions } = useHelpers();
  const { useValidators, useGeneral } = useQuickFunctions();
  const { newNotificationValidationShema } = useValidators();

  // Controllers HU66 Como Super Admin, quiero Formulario para así crear notificación.
  const { dateFormatUTC } = useGeneral();
  const { id } = useParams();

  // Actions
  const { useActions } = useApi();
  const { useNotificationsActions, useGeneralActions, useGroupsActions } = useActions();
  const {
    actGetNotification,
    actAddEditNotification,
    actGetMembeerGroups,
    actGetNotificationsEntities,
    actGetNotificationsEntitiesById,
  } = useNotificationsActions();
  const { actListGroupsMision } = useGroupsActions();

  const { control, handleSubmit, reset, setValue, getValues, formState: { errors } } = useForm({
    mode: "all",
    resolver: yupResolver(newNotificationValidationShema),
    defaultValues: {
      reminders: [{ date: "", time: "", ampm: "am" }],
      reminder: false,
      reminder_push: false,
      reminder_email: false,
    },
  });

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      console.log("Form validation errors:", errors);
    }
  }, [errors]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "reminders",
  });

  const { control: controlSearch, reset: resetSearch } = useForm();

  const loadData = async (data) => {
    let entityType = "Announcement";
    let entityId = null;
    let groupId = null;
    let groupType = null;
    let groupName = null;

    if (data.mission_id) {
      entityType = "Mission";
      entityId = data.mission_id;
    }

    if (data.article_id) {
      entityType = "Article";
      entityId = data.article_id;
    }

    if (data.membeer_group_id) {
      groupId = data.membeer_group_id;
      groupType = "MembeerGroup";
      groupName = data.entity_value;
    }

    if (data.membeer_id) {
      groupId = data.membeer_id;
      groupType = "Membeer";
      groupName = data.entity_value;
    }

    setValue("title", data.title);
    setValue("reminder", data.reminders && data.reminders.length > 0 ? true : false);
    setValue("publicDate", data.send_on);
    setValue("publicTime", data.send_on);
    setValue("publicAmpm", data.send_on);
    setValue("publicAmpm", data.send_on ? (new Date(data.send_on).getHours() >= 12 ? "pm" : "am") : "");
    setValue("message_push", data.push_message);
    setValue("reminder_push", data.push_enabled);
    setValue("reminder_email", data.email_enabled);
    setValue("message_email", data.email_body);
    setValue("header_image", `${process.env.REACT_APP_API_URL}${data.email_header_image?.replace(/^\//, '')}`);
    setValue("group", groupId);
    setValue("entity", entityType);
    setValue("email_subject", data.email_subject);

    setEntitySelected(entityId);

    // Set the selected group in the autocomplete field
    if (groupId && groupName) {
      resetSearch({
        search: { label: groupName, value: groupId, type: groupType }
      });
      setSelectedGroupName(groupName);
      setSelectedGroupType(groupType);
      setValue("group", groupId);
    }

    if (entityType) {
      actGetNotificationsEntitiesById(entityType, loadDataEntities);
    }

    if (data.reminders && data.reminders.length > 0) {
      remove();
      setValue(
        "reminders",
        data.reminders.map((x) => {
          return {
            id: x.id,
            date: x.send_on ? x.send_on : "",
            time: x.send_on ? dateFormatUTC([x.send_on, x.send_on]) : "",
            ampm: x.send_on ? (new Date(x.send_on).getHours() >= 12 ? "pm" : "am") : "",
          };
        })
      );
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetData();
  };

  const resetData = () => {
    reset();
  };

  const handleShowModal = () => {
    setShowModal(true);
  };

  const handleOnCreateSuccess = () => {
    handleShowModal();
    setTimeout(() => {
      navigation(-1);
    }, 2000);
  }

  const handleOnCreateError = () => {
    setSaving(false);
  }

  const onSubmit = (data) => {
    setSaving(true);
    let reminder = [];
    if (data.reminders && data.reminders.length > 0 && data.reminders[0].date != "") {
      reminder = data.reminders.map((x) => {
        return {
          send_on: dateFormatUTC([new Date(x.date), new Date(x.time), x.ampm])
        }
      });
    }

    const formData = new FormData();
    formData.append("title", data.title);
    formData.append("reminders_enabled", data.reminder ? 1 : 0);
    formData.append("push_message", data.message_push);
    formData.append("email_body", data.message_email);
    formData.append("email_subject", data.email_subject ? data.email_subject : "");
    formData.append("send_on", dateFormatUTC([data.publicDate, data.publicTime, data.publicAmpm]));
    formData.append("email_enabled", data.reminder_email ? 1 : 0);
    formData.append("push_enabled", data.reminder_push ? 1 : 0);
    formData.append("membeer_group_id", null);
    formData.append("membeer_id", null);
    formData.append("mission_id", null);
    formData.append("article_id", null);

    if (selectedGroupType === "MembeerGroup") {
      formData.set("membeer_group_id", id ? data.group : Number(data.group));
    }

    if (selectedGroupType === "Membeer") {
      formData.set("membeer_id", id ? data.group : Number(data.group));
    }

    if (data.entity == "Article") {
      formData.set("article_id", data.entity_id?.value);
    }

    if (data.entity == "Mission") {
      formData.set("mission_id", data.entity_id?.value);
    }

    if (reminder.length > 0) {
      formData.append("reminders", JSON.stringify(reminder));
    }

    if (data?.header_image?.length > 0 && typeof data?.header_image !== 'string') {
      formData.append("email_header_image", data.header_image[0]);
    }

    actAddEditNotification({ id, datos: formData, reminders: reminder }, handleOnCreateSuccess, handleOnCreateError);
  };

  const handleEntitiesSelectChange = (data) => {
    setValue("entity_id", null);
    if (data.target.value !== "Announcement")
      actGetNotificationsEntitiesById(data.target.value, loadDataEntities);
    else setListEntitiesById([]);
  };

  const handleSelectGroup = (data) => {
    if (data !== null) {
      console.log(data)
      setValue("group", data.value);
      setSelectedGroupName(data.label);
      setSelectedGroupType(data.type);
    }
  };

  const loadDataGroups = (data) => {
    setListGroups(
      data.data.map((v) => {
        return { ...v, label: v.name, value: v.id };
      })
    );
  };

  const onSubmitSearchGroup = (e) => {
    if (e.target.value.length >= 3) {
      actListGroupsMision({ search: e.target.value, data: {} }, loadDataGroups);
    } else {
      setListGroups([]);
    }
  };

  const onChangeSearchGroup = useCallback(debounce(onSubmitSearchGroup, 750), []);

  const loadDataEntities = (data) => {
    // Remove duplicates based on id
    const uniqueData = data.reduce((acc, current) => {
      const exists = acc.find(item => item.id === current.id);
      if (!exists) {
        acc.push(current);
      }
      return acc;
    }, []);

    setListEntitiesById(
      uniqueData.map((x) => {
        return { label: x.name, value: x.id };
      })
    );
  };

  //SET SELECTED VALUE IN LISTADO AFTER LOAD DATA IN THE LIST
  useEffect(() => {
    if (entitySelected) {
      const entity = listEntitiesById.filter((x) => x.value === entitySelected)[0];
      if (entity) setValue("entity_id", entity);
    }
  }, [listEntitiesById]);

  useEffect(() => {
    actGetNotificationsEntities((data) => {
      setListEntities(
        data.map((x) => {
          return { label: x.name, value: x.entity };
        })
      );
    });
    if (id) actGetNotification(id, loadData);
  }, []);

  return {
    id,
    control,
    handleSubmit,
    onSubmit,
    showModal,
    saving,
    handleCloseModal,
    listCountries,
    fields,
    append,
    remove,
    listGroups,
    listEntities,
    listEntitiesById,
    handleEntitiesSelectChange,
    handleSelectGroup,
    controlSearch,
    onChangeSearchGroup,
  };
};

export default useNotificationCreate;
